import { officeErrorToLogObject } from "../utils/officeJsDebugLog";
import { setDocumentChangeTrackingOffStrict } from "./officeService";

/**
 * Load a .docx into the current Word window (host document) from base64.
 *
 * `Office.context.document.openAsync` is not implemented for Word task-pane hosts
 * (common desktop + web), so we use Word.js `insertFileFromBase64`.
 *
 * We prefer `document.insertFileFromBase64` (WordApi 1.5+) with conservative
 * `InsertFileOptions` to avoid RichApi GeneralException from style/theme/custom-XML
 * merge, then fall back to body-only insertion and finally `body.clear()` if needed.
 */

async function ensureOfficeReady(): Promise<void> {
  if (typeof Office === "undefined") {
    throw new Error("Office.js is not loaded");
  }
  await Office.onReady();
}

/** Strip accidental data-URL prefix and whitespace so Word receives raw standard base64. */
function normalizeBase64ForWord(raw: string): string {
  let s = raw.trim();
  const dataPrefix = /^data:[^;]+;base64,/i;
  if (dataPrefix.test(s)) {
    s = s.replace(dataPrefix, "");
  }
  return s.replace(/\s+/g, "");
}

const LOG_PREFIX = "[openDraftInHost]";
const CLEAR_LOG_PREFIX = "[clearHostDocument]";

/** Shown when Word is in Viewing mode or otherwise blocks edits required to open a contract. */
export const HOST_DOCUMENT_VIEW_ONLY_MESSAGE =
  "Word is in Viewing mode. Switch to Edit mode in the top bar before opening a contract.";

function formatOfficeJsError(err: unknown): string {
  if (err instanceof Error) {
    const anyErr = err as Error & {
      code?: string;
      debugInfo?: {
        code?: string;
        errorLocation?: string;
        fullStatements?: string[];
      };
    };
    const parts = [anyErr.message];
    if (anyErr.code) {
      parts.push(`code=${anyErr.code}`);
    }
    const d = anyErr.debugInfo;
    if (d) {
      if (d.code) {
        parts.push(`debugCode=${d.code}`);
      }
      if (d.errorLocation) {
        parts.push(`at=${d.errorLocation}`);
      }
      if (d.fullStatements?.length) {
        parts.push(`statements=${d.fullStatements.join(" | ")}`);
      }
    }
    return parts.join("; ");
  }
  return String(err);
}

function isGeneralOfficeJsException(err: unknown): boolean {
  return /generalexception/i.test(formatOfficeJsError(err));
}

function isLikelyHostViewOnlyOrReadOnlyError(err: unknown): boolean {
  const haystack = formatOfficeJsError(err).toLowerCase();
  return (
    /readonlyexception/.test(haystack) ||
    /read[\s-]?only/.test(haystack) ||
    /viewing/.test(haystack) ||
    /cannot edit/.test(haystack) ||
    /not allowed.*edit/.test(haystack) ||
    (/generalexception/.test(haystack) &&
      /access denied|permission|protected/i.test(haystack))
  );
}

function throwIfHostViewOnlyError(err: unknown): never {
  if (err instanceof Error && err.message === HOST_DOCUMENT_VIEW_ONLY_MESSAGE) {
    throw err;
  }
  if (
    isLikelyHostViewOnlyOrReadOnlyError(err) ||
    isGeneralOfficeJsException(err)
  ) {
    throw new Error(HOST_DOCUMENT_VIEW_ONLY_MESSAGE, { cause: err });
  }
  throw err instanceof Error ? err : new Error(String(err), { cause: err });
}

function isHostViewOnlyError(err: unknown): boolean {
  if (err instanceof Error && err.message === HOST_DOCUMENT_VIEW_ONLY_MESSAGE) {
    return true;
  }
  return (
    isLikelyHostViewOnlyOrReadOnlyError(err) || isGeneralOfficeJsException(err)
  );
}

/**
 * Verifies the host document accepts edits (not Word Viewing mode / read-only protection).
 * Office.js has no direct Viewing-mode API; a lightweight insert/delete probe is used.
 */
export const assertHostDocumentEditable = async (): Promise<void> => {
  await ensureOfficeReady();

  if (typeof Word === "undefined") {
    return;
  }

  try {
    await Word.run(async (context) => {
      if (
        typeof Office !== "undefined" &&
        Office.context?.requirements?.isSetSupported?.("WordApiDesktop", "1.4")
      ) {
        context.document.load("protectionType");
        await context.sync();
        if (
          context.document.protectionType ===
          Word.ProtectionType.allowOnlyReading
        ) {
          throw new Error(HOST_DOCUMENT_VIEW_ONLY_MESSAGE);
        }
      }

      const probe = context.document.body.getRange(Word.RangeLocation.start);
      probe.insertText("\u200B", Word.InsertLocation.after);
      await context.sync();
      probe.delete();
      await context.sync();
    });
  } catch (err: unknown) {
    if (!isHostViewOnlyError(err)) {
      console.warn(LOG_PREFIX, "editability probe skipped — inconclusive", {
        summary: formatOfficeJsError(err),
      });
      return;
    }
    throwIfHostViewOnlyError(err);
  }
};

/** Lower-risk import flags (helps Word on the web and heavy templates). */
const INSERT_FILE_OPTS_BALANCED: Word.InsertFileOptions = {
  importTheme: false,
  importStyles: true,
  importParagraphSpacing: true,
  importPageColor: false,
  importChangeTrackingMode: false,
  importCustomProperties: false,
  importCustomXmlParts: false,
  importDifferentOddEvenPages: false,
};

/**
 * After replacing the document from base64, Word sometimes leaves the caret/selection in a state
 * that blocks normal scrolling and paging. Collapse selection to the start of the body so the
 * canvas regains expected navigation.
 */
async function stabilizeViewportAfterBase64Insert(): Promise<void> {
  try {
    await Word.run(async (context) => {
      const startRange = context.document.body.getRange(
        Word.RangeLocation.start,
      );
      startRange.select(Word.SelectionMode.start);
      await context.sync();
    });
  } catch {
    // Selection APIs are restricted on some hosts; opening the file must still count as success.
  }
}

/** Minimal merge — may flatten some custom styles but often succeeds when balanced fails. */
const INSERT_FILE_OPTS_MINIMAL: Word.InsertFileOptions = {
  importTheme: false,
  importStyles: false,
  importParagraphSpacing: false,
  importPageColor: false,
  importChangeTrackingMode: false,
  importCustomProperties: false,
  importCustomXmlParts: false,
  importDifferentOddEvenPages: false,
};

/** Remove all content from the host document so Word shows a blank canvas. */
export const clearHostDocument = async (): Promise<void> => {
  await ensureOfficeReady();

  if (typeof Word === "undefined") {
    return;
  }

  await assertHostDocumentEditable();

  console.info(CLEAR_LOG_PREFIX, "start");
  try {
    await Word.run(async (context) => {
      context.document.body.clear();
      await context.sync();
    });
    await stabilizeViewportAfterBase64Insert();
    console.info(CLEAR_LOG_PREFIX, "success");
  } catch (err: unknown) {
    console.warn(CLEAR_LOG_PREFIX, "failed", officeErrorToLogObject(err));
    if (isHostViewOnlyError(err)) {
      throwIfHostViewOnlyError(err);
    }
    throw err instanceof Error ? err : new Error(String(err), { cause: err });
  }
};

export const openDraftInHost = async (base64: string): Promise<void> => {
  if (!base64?.trim()) {
    throw new Error("Missing document content");
  }

  await ensureOfficeReady();

  if (typeof Word === "undefined") {
    throw new Error("Word.js API is not available in this host");
  }

  const trackingOff = await setDocumentChangeTrackingOffStrict();
  if (!trackingOff.ok) {
    throw new Error(
      `Unable to open the contract while Track Changes is enabled or locked. ${trackingOff.message}`.slice(
        0,
        340,
      ),
    );
  }

  await assertHostDocumentEditable();

  const normalized = normalizeBase64ForWord(base64);
  if (!normalized) {
    throw new Error("Missing document content after normalizing base64");
  }

  const rawLen = base64.trim().length;
  const normLen = normalized.length;
  const approxBytes = Math.floor((normLen * 3) / 4);
  // .docx (zip) as base64 often starts with "UEs" (PK\x03\x04).
  const b64Prefix = normalized.slice(0, 12);
  console.info(LOG_PREFIX, "start", {
    rawBase64Length: rawLen,
    normalizedBase64Length: normLen,
    approxDecodedBytes: approxBytes,
    base64Prefix: b64Prefix,
    likelyZip: b64Prefix.startsWith("UEs"),
    officeHost:
      typeof Office !== "undefined" ? Office.context?.host : undefined,
    platform:
      typeof Office !== "undefined" ? Office.context?.platform : undefined,
    wordApiRequirement:
      typeof Office !== "undefined"
        ? Office.context?.requirements?.isSetSupported?.("WordApi", "1.5")
        : undefined,
  });

  const attempts: Array<{
    label: string;
    run: (context: Word.RequestContext) => Promise<void>;
  }> = [
    {
      label: "document.insertFileFromBase64(replace, balanced options)",
      run: async (context) => {
        context.document.insertFileFromBase64(
          normalized,
          Word.InsertLocation.replace,
          INSERT_FILE_OPTS_BALANCED,
        );
        await context.sync();
      },
    },
    {
      label: "document.insertFileFromBase64(replace, minimal import)",
      run: async (context) => {
        context.document.insertFileFromBase64(
          normalized,
          Word.InsertLocation.replace,
          INSERT_FILE_OPTS_MINIMAL,
        );
        await context.sync();
      },
    },
    {
      label: "body.insertFileFromBase64(replace) without clear",
      run: async (context) => {
        context.document.body.insertFileFromBase64(
          normalized,
          Word.InsertLocation.replace,
        );
        await context.sync();
      },
    },
    {
      label: "body.clear then body.insertFileFromBase64(replace)",
      run: async (context) => {
        const body = context.document.body;
        body.clear();
        await context.sync();
        body.insertFileFromBase64(normalized, Word.InsertLocation.replace);
        await context.sync();
      },
    },
  ];

  const errors: string[] = [];

  for (let i = 0; i < attempts.length; i++) {
    const attempt = attempts[i];
    console.info(LOG_PREFIX, `attempt ${i + 1}/${attempts.length}`, {
      label: attempt.label,
    });
    try {
      await Word.run(async (context) => {
        await attempt.run(context);
      });
      console.info(LOG_PREFIX, "success", {
        label: attempt.label,
        attemptIndex: i + 1,
      });
      await stabilizeViewportAfterBase64Insert();
      console.info(
        LOG_PREFIX,
        "viewport stabilize (selection → body start) done",
      );
      return;
    } catch (err: unknown) {
      const summary = formatOfficeJsError(err);
      errors.push(`${attempt.label}: ${summary}`);
      console.warn(LOG_PREFIX, "attempt failed", {
        label: attempt.label,
        attemptIndex: i + 1,
        summary,
      });
      console.warn(
        LOG_PREFIX,
        "attempt failed — full error object",
        officeErrorToLogObject(err),
      );
      if (err instanceof Error && err.cause !== undefined) {
        console.warn(
          LOG_PREFIX,
          "attempt failed — error.cause",
          officeErrorToLogObject(err.cause),
        );
      }
    }
  }

  if (errors.every((summary) => /generalexception/i.test(summary))) {
    console.error(LOG_PREFIX, "all attempts failed — likely view-only host", {
      errors,
    });
    throw new Error(HOST_DOCUMENT_VIEW_ONLY_MESSAGE, { cause: errors });
  }

  const hint =
    "Tips: use **Edit** mode (not Viewing), exit Protected View if shown, update Microsoft 365 / Word, and try desktop Word if you are on the web. " +
    "If every strategy failed, the .docx may contain constructs this host cannot merge.";
  const finalMessage = `Could not load the document into Word after ${attempts.length} attempts. ${hint} Details: ${errors.join(" → ")}`;
  console.error(LOG_PREFIX, "all attempts failed", { errors, finalMessage });
  throw new Error(finalMessage, { cause: errors });
};
